import { Trans } from "react-i18next";

import { ReactComponent as advancedSVG } from "@/assets/icons/fitness-level/advanced.svg";
import { ReactComponent as beginnerSVG } from "@/assets/icons/fitness-level/beginner.svg";
import { ReactComponent as intermediateSVG } from "@/assets/icons/fitness-level/intermediate.svg";
import { ReactComponent as achievementsSVG } from "@/assets/icons/motivation/achievements.svg";
import { ReactComponent as challengesSVG } from "@/assets/icons/motivation/challenges.svg";
import { ReactComponent as challengingSVG } from "@/assets/icons/motivation/challenging.svg";
import { ReactComponent as disciplineSVG } from "@/assets/icons/motivation/discipline.svg";
import { ReactComponent as enjoyableSVG } from "@/assets/icons/motivation/enjoyable.svg";
import { ReactComponent as fickleSVG } from "@/assets/icons/motivation/fickle.svg";
import { ReactComponent as inconvenientSVG } from "@/assets/icons/motivation/inconvenient.svg";
import { ReactComponent as musicSVG } from "@/assets/icons/motivation/music.svg";
import { ReactComponent as necessarySVG } from "@/assets/icons/motivation/necessary.svg";
import { ReactComponent as progressSVG } from "@/assets/icons/motivation/progress-tracking.svg";
import { ReactComponent as resilientSVG } from "@/assets/icons/motivation/resilient.svg";
import { ReactComponent as steadfastSVG } from "@/assets/icons/motivation/steadfast.svg";
import { ReactComponent as basketballSVG } from "@/assets/icons/other-sports/basketball.svg";
import { ReactComponent as boxingSVG } from "@/assets/icons/other-sports/boxing.svg";
import { ReactComponent as cyclingSVG } from "@/assets/icons/other-sports/cycling.svg";
import { ReactComponent as yogaSVG } from "@/assets/icons/other-sports/yoga.svg";
import { getDateOneMonthFromNow } from "@/components/Questions/Date";
import { EventAnswersLabels } from "@/constants/answersLabels";
import {
    BodyInjures,
    MALE_BODY_INJURES_IMG,
    MALE_BODY_INJURES_IMG_SELECTED,
} from "@/constants/body-injures";
import {
    BodyMetricTypes,
    ImagesByBodyFatRange,
    MaleBodyFatRangeTypes,
} from "@/constants/bodyMetrics";
import { BodyTypes } from "@/constants/bodyType";
import { EventScreenTypes } from "@/constants/event";
import {
    calisthenicsEventTextByGoal,
    FitnessGoals,
    goalIngTextById,
    goalTextById,
    goalTextByIdChartImage,
} from "@/constants/fitnessGoal";
import { FitnessLevels, getFitnessLevelLabel } from "@/constants/fitnessLevels";
import { questionGroups } from "@/constants/questionGroups";
import { QUESTIONS_IDS, QuestionTypes } from "@/constants/questionsIds";
import i18n from "@/localization/i18next";
import bodyArms from "@/male/assets/body-injures/arms.webp";
import bodyBack from "@/male/assets/body-injures/back.webp";
import bodyElbows from "@/male/assets/body-injures/elbows.webp";
import bodyFeet from "@/male/assets/body-injures/feet.webp";
import bodyKnees from "@/male/assets/body-injures/knees.webp";
import bodyLegs from "@/male/assets/body-injures/legs.webp";
import bodyNeck from "@/male/assets/body-injures/neck.webp";
import bodyNoIssues from "@/male/assets/body-injures/no-issues.webp";
import bodyShoulders from "@/male/assets/body-injures/shoulders.webp";
import ecto from "@/male/assets/body-type/ecto.webp";
import endo from "@/male/assets/body-type/endo.webp";
import meso from "@/male/assets/body-type/meso.webp";
import coreCalisthenics from "@/male/assets/calisthenics/core.webp";
import kneeCalisthenics from "@/male/assets/calisthenics/knee.webp";
import pushupCalisthenics from "@/male/assets/calisthenics/pushup.webp";
import sidePlank from "@/male/assets/calisthenics/side-plank.webp";
import stretchCalisthenics from "@/male/assets/calisthenics/stretch.webp";
import { ReactComponent as liftingSvg } from "@/male/assets/goals-icons/lifting.svg";
import { ReactComponent as runSvg } from "@/male/assets/goals-icons/run.svg";
import lean from "@/male/assets/target-body-type/lean.webp";
import muscular from "@/male/assets/target-body-type/muscular.webp";
import ripped from "@/male/assets/target-body-type/ripped.webp";
import {
    getCalisthenicsTextByFitnessLevel,
    getCalisthenicsTextByHealthRestrictions,
    getCalisthenicsTextByObstacle,
    getCalisthenicsTextByTrainingFrequency,
    getCalisthenicTextByPrimaryGoal,
} from "@/male/utils/calisthenics-texts";
import {
    EventAnswers,
    OtherSportsAnswers,
    PushUpsSquartsAnswers,
    SleepAmountAnswers,
} from "@/types/answers";
import { Genders } from "@/types/gender";
import { Answers } from "@/utils/AnswersProvider";

import WristBodyType from "../../components/WristBodyType";

export const getQuestions = (prefix: string, answers: Answers) => [
    {
        id: QUESTIONS_IDS.PRIMARY_GOAL,
        event: `${prefix}_${QUESTIONS_IDS.PRIMARY_GOAL}`,
        type: QuestionTypes.RadioImageLeft,
        title: i18n.t("fitness_goal_prompt"),
        subtitle: i18n.t("goal_motivation_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: FitnessGoals.GainMuscle,
                text: "Gain Muscle",
                svg: liftingSvg,
            },
            {
                id: FitnessGoals.LoseWeight,
                text: "Lose Weight",
                svg: runSvg,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CALISTHENICS_GOAL_FEEDBACK,
        type: QuestionTypes.CalisthenicsFeedbackLoops,
        groupTitle: questionGroups.Goals,
        event: `${prefix}_${QUESTIONS_IDS.CALISTHENICS_GOAL_FEEDBACK}`,
        img: sidePlank,
        title: getCalisthenicTextByPrimaryGoal[
            answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
        ]?.title,
        description:
            getCalisthenicTextByPrimaryGoal[answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals]
                ?.description,
    },
    ...((answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as string) === FitnessGoals.BeHealthy
        ? []
        : [
              {
                  id: QUESTIONS_IDS.BIGGEST_DRIVE,
                  event: `${prefix}_${EventScreenTypes.BIGGEST_DRIVE}`,
                  type: QuestionTypes.Checkbox,
                  title: i18n.t("biggest_drive_prompt", {
                      goal: goalIngTextById[answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals],
                  }),
                  subtitle: i18n.t("biggest_drive_motivation_prompt"),
                  groupTitle: questionGroups.Goals,
                  answers: [
                      {
                          id: "appearance",
                          text: i18n.t("physical_appearance"),
                      },
                      {
                          id: "enjoying_time",
                          text: i18n.t("enjoying_time"),
                      },
                      {
                          id: "getting_healthier",
                          text: i18n.t("getting_healthier"),
                      },
                      {
                          id: "feeling_happier",
                          text: i18n.t("feeling_happier"),
                      },
                      {
                          id: "other",
                          text: i18n.t("other"),
                      },
                  ],
              },
          ]),
    {
        id: QUESTIONS_IDS.DESIRED_BODY,
        type: QuestionTypes.RadioImageLeft,
        event: `${prefix}_desired_body`,
        title: i18n.t("desired_body_prompt"),
        subtitle: i18n.t("desired_body_motivation_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: BodyTypes.Lean,
                text: i18n.t("lean"),
                description: i18n.t("lean_description"),
                img: lean,
            },
            {
                id: BodyTypes.Muscular,
                text: i18n.t("muscular"),
                description: i18n.t("muscular_description"),
                img: muscular,
            },
            {
                id: BodyTypes.Ripped,
                text: i18n.t("ripped"),
                description: i18n.t("ripped_description"),
                img: ripped,
            },
        ],
    },
    {
        id: "familiar_calisthenics",
        event: `${prefix}_familiar_calisthenics`,
        type: QuestionTypes.Radio,
        title: "How familiar are you with calisthenics?",
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "starting",
                text: "I’m just starting",
            },
            {
                id: "some_experience",
                text: "I have some experience",
            },
            {
                id: "proficient",
                text: "I am proficient",
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SUCCESS_MEASURE,
        event: `${prefix}_${QUESTIONS_IDS.SUCCESS_MEASURE}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("success_measure_prompt"),
        subtitle: i18n.t("success_measure_motivation_prompt"),
        groupTitle: questionGroups.Goals,
        maxChosenOptions: 3,
        answers: [
            {
                id: "muscle",
                text: i18n.t("muscle_gain"),
                description: i18n.t("building_muscle"),
            },
            {
                id: "strength",
                text: i18n.t("increased_strength"),
                description: i18n.t("lifting_more_weight"),
            },
            {
                id: "endurance",
                text: i18n.t("improved_endurance"),
                description: i18n.t("achieving_lower_body_fat"),
            },
            {
                id: "weight_loss",
                text: i18n.t("weight_loss"),
                description: i18n.t("seeing_lower_numbers_on_scale"),
            },
            {
                id: "measurements",
                text: i18n.t("body_measurements"),
                description: i18n.t("tracking_changes_in_size"),
            },
            {
                id: "improvements",
                text: i18n.t("fitness_level_improvements"),
                description: i18n.t("progress_in_overall_fitness"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.TARGET_AREAS,
        event: `${prefix}_${QUESTIONS_IDS.TARGET_AREAS}`,
        type: QuestionTypes.CheckboxInline,
        title: i18n.t("focus_areas_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: BodyInjures.FullBody,
                text: i18n.t("full_body"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.FullBody],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.FullBody],
            },
            {
                id: BodyInjures.Shoulders,
                text: i18n.t("shoulders"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Shoulders],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Shoulders],
            },
            {
                id: BodyInjures.Arms,
                text: i18n.t("massive_biceps"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Arms],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Arms],
            },
            {
                id: BodyInjures.Core,
                text: i18n.t("wider_back"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Core],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Core],
            },
            {
                id: BodyInjures.Chest,
                text: i18n.t("chest"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Chest],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Chest],
            },
            {
                id: BodyInjures.Abs,
                text: i18n.t("six_pack"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Abs],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Abs],
            },
            {
                id: BodyInjures.Glutes,
                text: i18n.t("tight_glutes"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Glutes],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Glutes],
            },
            {
                id: BodyInjures.Legs,
                text: i18n.t("strong_legs"),
                img: MALE_BODY_INJURES_IMG[BodyInjures.Legs],
                selectedImg: MALE_BODY_INJURES_IMG_SELECTED[BodyInjures.Legs],
            },
        ],
    },
    {
        id: QUESTIONS_IDS.HOPE_TO_FEEL,
        event: `${prefix}_${QUESTIONS_IDS.HOPE_TO_FEEL}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("hope_to_feel_prompt"),
        groupTitle: questionGroups.Goals,
        answers: [
            {
                id: "energized",
                text: i18n.t("energized"),
                description: i18n.t("increased_energy_vitality"),
            },
            {
                id: "confident",
                text: i18n.t("confident"),
                description: i18n.t("confident_description"),
            },
            {
                id: "relaxed",
                text: i18n.t("relaxed"),
                description: i18n.t("relaxed_description"),
            },
            {
                id: "accomplished",
                text: i18n.t("accomplished"),
                description: i18n.t("accomplished_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.IMPORTANT_ELEMENTS,
        event: `${prefix}_${QUESTIONS_IDS.IMPORTANT_ELEMENTS}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("important_elements_prompt"),
        subtitle: i18n.t("important_elements_motivation_prompt"),
        groupTitle: questionGroups.CalisthenicsWorkoutPreferences,
        answers: [
            {
                id: "consistency",
                text: i18n.t("consistency"),
                description: i18n.t("consistency_description"),
            },
            {
                id: "commitment",
                text: i18n.t("commitment"),
                description: i18n.t("commitment_description"),
            },
            {
                id: "guidance",
                text: i18n.t("guidance"),
                description: i18n.t("guidance_description"),
            },
            {
                id: "exercise_variety",
                text: i18n.t("exercise_variety"),
                description: i18n.t("exercise_variety_description"),
            },
            {
                id: "lifestyle_changes",
                text: i18n.t("lifestyle_changes"),
                description: i18n.t("lifestyle_changes_description"),
            },
            {
                id: "goal_focus",
                text: i18n.t("goal_focus"),
                description: i18n.t("goal_focus_description", {
                    goal: goalIngTextById[answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals],
                }),
            },
            {
                id: "convenience",
                text: i18n.t("convenience"),
                description: i18n.t("convenience_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SPORT_PREFER,
        event: `${prefix}_${QUESTIONS_IDS.SPORT_PREFER}`,
        type: QuestionTypes.Radio,
        title: i18n.t("workout_preference_prompt"),
        groupTitle: questionGroups.CalisthenicsWorkoutPreferences,
        answers: [
            {
                id: "morning",
                text: i18n.t("morning"),
            },
            {
                id: "afternoon",
                text: i18n.t("afternoon"),
            },
            {
                id: "evening",
                text: i18n.t("evening"),
            },
            {
                id: "different",
                text: i18n.t("different_times"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DESIRED_WORKOUT_TIME,
        event: `${prefix}_${QUESTIONS_IDS.DESIRED_WORKOUT_TIME}`,
        type: QuestionTypes.Radio,
        title: i18n.t("desired_workout_time_prompt"),
        groupTitle: questionGroups.CalisthenicsWorkoutPreferences,
        answers: [
            {
                id: "30",
                text: i18n.t("30_min_or_less"),
            },
            {
                id: "45",
                text: i18n.t("45_min_or_less"),
            },
            {
                id: "60",
                text: i18n.t("one_hour_or_so"),
            },
            {
                id: "60+",
                text: i18n.t("over_an_hour"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.OTHER_SPORTS,
        event: `${prefix}_${QUESTIONS_IDS.OTHER_SPORTS}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("other_sports_prompt"),
        groupTitle: questionGroups.CalisthenicsWorkoutPreferences,
        answers: [
            {
                id: OtherSportsAnswers.No,
                text: i18n.t("no"),
            },
            {
                id: OtherSportsAnswers.Cardio,
                text: i18n.t("cardio"),
                description: i18n.t("cardio_types"),
                svg: cyclingSVG,
            },
            {
                id: OtherSportsAnswers.Flexibility,
                text: i18n.t("flexibility"),
                description: i18n.t("flexibility_types"),
                svg: yogaSVG,
            },
            {
                id: OtherSportsAnswers.MartialArts,
                text: i18n.t("martial_arts"),
                description: i18n.t("martial_arts_types"),
                svg: boxingSVG,
            },
            {
                id: OtherSportsAnswers.GameSports,
                text: i18n.t("game_sports"),
                description: i18n.t("game_sports_types"),
                svg: basketballSVG,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CORTISOL,
        type: "cortisol_screen",
        progressBarBreakPoint: true,
        event: `${prefix}_cortisol`,
    },
    {
        id: QUESTIONS_IDS.BODY_TYPE,
        type: QuestionTypes.RadioImageLeft,
        event: `${prefix}_${QUESTIONS_IDS.BODY_TYPE}`,
        title: i18n.t("body_type_prompt"),
        subtitle: i18n.t("body_type_motivation_prompt"),
        groupTitle: questionGroups.Personal,
        answers: [
            {
                id: "skinny",
                text: i18n.t("ectomorph"),
                description: i18n.t("ectomorph_description"),
                img: ecto,
            },
            {
                id: "regular",
                text: i18n.t("mesomorph"),
                description: i18n.t("mesomorph_description"),
                img: meso,
            },
            {
                id: "extra",
                text: i18n.t("endomorph"),
                description: i18n.t("endomorph_description"),
                img: endo,
            },
        ],
        extraBottomComponent: <WristBodyType />,
    },
    {
        id: QUESTIONS_IDS.BODY_FAT,
        type: QuestionTypes.Range,
        event: `${prefix}_${QUESTIONS_IDS.BODY_FAT}`,
        groupTitle: questionGroups.Personal,
        title: i18n.t("body_fat_level_prompt"),
        answers: Object.values(MaleBodyFatRangeTypes).map((type) => ({
            id: type,
            value: type,
            img: ImagesByBodyFatRange[type],
        })),
    },
    {
        id: QUESTIONS_IDS.HEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.HEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.HEIGHT,
        title: i18n.t("height_prompt"),
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.CURRENT_WEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.CURRENT_WEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.WEIGHT,
        title: i18n.t("current_weight_prompt"),
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.TARGET_WEIGHT,
        event: `${prefix}_${QUESTIONS_IDS.TARGET_WEIGHT}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.WEIGHT,
        weightType: "target",
        title: i18n.t("target_weight_prompt"),
        groupTitle: questionGroups.Personal,
    },
    {
        id: QUESTIONS_IDS.AGE,
        event: `${prefix}_${QUESTIONS_IDS.AGE}`,
        type: QuestionTypes.Input,
        inputType: BodyMetricTypes.AGE,
        title: i18n.t("age_prompt"),
        groupTitle: questionGroups.Personal,
    },
    ...((answers?.[QUESTIONS_IDS.AGE] as number) > 35
        ? [
              {
                  id: QUESTIONS_IDS.PROTECT_AGAINST_AGING,
                  event: `${prefix}_${QUESTIONS_IDS.PROTECT_AGAINST_AGING}`,
                  type: QuestionTypes.Radio,
                  title: i18n.t("protect_against_aging_prompt"),
                  groupTitle: questionGroups.WorkoutSettings,
                  answers: [
                      {
                          id: "yes",
                          text: i18n.t("yes"),
                      },
                      {
                          id: "no",
                          text: i18n.t("no"),
                      },
                  ],
              },
          ]
        : []),
    {
        id: QUESTIONS_IDS.FITNESS_LEVEL,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_LEVEL}`,
        type: QuestionTypes.Radio,
        title: i18n.t("fitness_level_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: FitnessLevels.Beginner,
                text: getFitnessLevelLabel(FitnessLevels.Beginner, Genders.Male),
                description: i18n.t("beginner_description", {
                    context: Genders.Male,
                    defaultValue: "beginner_description",
                }),
                svg: beginnerSVG,
            },
            {
                id: FitnessLevels.Intermediate,
                text: getFitnessLevelLabel(FitnessLevels.Intermediate, Genders.Male),
                description: i18n.t("intermediate_description"),
                svg: intermediateSVG,
            },
            {
                id: FitnessLevels.Advanced,
                text: getFitnessLevelLabel(FitnessLevels.Advanced, Genders.Male),
                description: i18n.t("advanced_description"),
                svg: advancedSVG,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CALISTHENICS_LEVEL_FEEDBACK,
        type: QuestionTypes.CalisthenicsFeedbackLoops,
        groupTitle: questionGroups.Fitness,
        event: `${prefix}_${QUESTIONS_IDS.CALISTHENICS_LEVEL_FEEDBACK}`,
        img: coreCalisthenics,
        ...getCalisthenicsTextByFitnessLevel[
            answers?.[QUESTIONS_IDS.FITNESS_LEVEL] as FitnessLevels
        ],
    },
    {
        id: QUESTIONS_IDS.TRAIN_3_MONTHS,
        event: `${prefix}_${QUESTIONS_IDS.TRAIN_3_MONTHS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("train_3months_prompt"),
        subtitle: i18n.t("train_3months_motivation_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: "hardly_ever",
                text: i18n.t("hardly_ever"),
            },
            {
                id: "1_2_per_week",
                text: i18n.t("1_2_per_week"),
            },
            {
                id: "3_4_per_week",
                text: i18n.t("3_4_per_week"),
            },
            {
                id: "5_6_per_week",
                text: i18n.t("5_6_per_week"),
            },
            {
                id: "everyday",
                text: i18n.t("everyday"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CALISTHENICS_TRAINING_FREQUENCY_FEEDBACK,
        type: QuestionTypes.CalisthenicsFeedbackLoops,
        groupTitle: questionGroups.Fitness,
        event: `${prefix}_${QUESTIONS_IDS.CALISTHENICS_TRAINING_FREQUENCY_FEEDBACK}`,
        img: pushupCalisthenics,
        ...getCalisthenicsTextByTrainingFrequency[
            answers?.[
                QUESTIONS_IDS.TRAIN_3_MONTHS
            ] as keyof typeof getCalisthenicsTextByTrainingFrequency
        ],
    },
    {
        id: QUESTIONS_IDS.FITNESS_EXPERIENCE,
        event: `${prefix}_${QUESTIONS_IDS.FITNESS_EXPERIENCE}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("fitness_experience_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: "personal_trainers",
                text: i18n.t("personal_trainers"),
            },
            {
                id: "fitness_apps",
                text: i18n.t("fitness_apps"),
            },
            {
                id: "workout_videos",
                text: i18n.t("workout_videos"),
            },
            {
                id: "self_taught",
                text: i18n.t("self_taught"),
            },
            {
                id: "none",
                text: i18n.t("none"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.HEALTH_PROBLEMS,
        event: `${prefix}_${QUESTIONS_IDS.HEALTH_PROBLEMS}`,
        type: QuestionTypes.CheckboxImageLeft,
        title: i18n.t("injuries_prompt"),
        subtitle: i18n.t("injuries_motivation_prompt"),
        groupTitle: questionGroups.Fitness,
        emptyAnswer: {
            id: "none",
            text: i18n.t("none"),
            img: bodyNoIssues,
        },
        answers: [
            {
                id: "backPain",
                text: i18n.t("back"),
                img: bodyBack,
            },
            {
                id: "kneePain",
                text: i18n.t("knees"),
                img: bodyKnees,
            },
            {
                id: "shoulderPain",
                text: i18n.t("shoulders"),
                img: bodyShoulders,
            },
            {
                id: "neckPain",
                text: i18n.t("neck"),
                img: bodyNeck,
            },
            {
                id: "armPain",
                text: i18n.t("arms"),
                img: bodyArms,
            },
            {
                id: "elbowPain",
                text: i18n.t("elbows"),
                img: bodyElbows,
            },
            {
                id: "legPain",
                text: i18n.t("legs"),
                img: bodyLegs,
            },
            {
                id: "footPain",
                text: i18n.t("feet"),
                img: bodyFeet,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CALISTHENICS_RESTRICTIONS_FEEDBACK,
        type: QuestionTypes.CalisthenicsFeedbackLoops,
        groupTitle: questionGroups.Fitness,
        event: `${prefix}_${QUESTIONS_IDS.CALISTHENICS_RESTRICTIONS_FEEDBACK}`,
        img: stretchCalisthenics,
        ...getCalisthenicsTextByHealthRestrictions(
            answers?.[QUESTIONS_IDS.HEALTH_PROBLEMS] as string[]
        ),
    },
    {
        id: QUESTIONS_IDS.ACTIVITY_ENGAGED,
        event: `${prefix}_${QUESTIONS_IDS.ACTIVITY_ENGAGED}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("activity_engaged_prompt"),
        subtitle: i18n.t("activity_engaged_motivation_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: "nutrition_changes",
                text: i18n.t("nutrition_changes"),
                description: i18n.t("nutrition_changes_types"),
            },
            {
                id: "weight_training",
                text: i18n.t("weight_training"),
                description: i18n.t("weight_training_types"),
            },
            {
                id: "cardio",
                text: i18n.t("cardio"),
                description: i18n.t("cardio_types"),
            },
            {
                id: "bodyweight_training",
                text: i18n.t("bodyweight_training"),
                description: i18n.t("bodyweight_training_types"),
            },
            {
                id: "group_sports",
                text: i18n.t("group_sports"),
                description: i18n.t("group_sports_types"),
            },
            {
                id: "martial_arts",
                text: i18n.t("martial_arts"),
                description: i18n.t("martial_arts_types"),
            },
            {
                id: "other",
                text: i18n.t("other"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PUSH_UPS,
        event: `${prefix}_${QUESTIONS_IDS.PUSH_UPS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("push_ups_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: PushUpsSquartsAnswers.Few,
                text: i18n.t("fewer_than_12"),
            },
            {
                id: PushUpsSquartsAnswers.Average,
                text: i18n.t("13_to_20"),
            },
            {
                id: PushUpsSquartsAnswers.Lot,
                text: i18n.t("more_than_20"),
            },
            {
                id: PushUpsSquartsAnswers.NoInfo,
                text: i18n.t("dont_know"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SQUATS,
        event: `${prefix}_${QUESTIONS_IDS.SQUATS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("squats_prompt"),
        groupTitle: questionGroups.Fitness,
        answers: [
            {
                id: PushUpsSquartsAnswers.Few,
                text: i18n.t("fewer_than_12"),
            },
            {
                id: PushUpsSquartsAnswers.Average,
                text: i18n.t("13_to_20"),
            },
            {
                id: PushUpsSquartsAnswers.Lot,
                text: i18n.t("more_than_20"),
            },
            {
                id: PushUpsSquartsAnswers.NoInfo,
                text: i18n.t("dont_know"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.BMI_RESULTS,
        type: QuestionTypes.BmiPromoMale,
        event: `${prefix}_${QUESTIONS_IDS.BMI_RESULTS}`,
        progressBarBreakPoint: true,
    },
    {
        id: QUESTIONS_IDS.MOTIVATION,
        event: `${prefix}_${QUESTIONS_IDS.MOTIVATION}`,
        type: QuestionTypes.Radio,
        title: i18n.t("motivation_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "necessary",
                text: i18n.t("necessary"),
                description: i18n.t("necessary_description"),
                svg: necessarySVG,
            },
            {
                id: "inconvenient",
                text: i18n.t("inconvenient"),
                description: i18n.t("inconvenient_description"),
                svg: inconvenientSVG,
            },
            {
                id: "enjoyable",
                text: i18n.t("enjoyable"),
                description: i18n.t("enjoyable_description"),
                svg: enjoyableSVG,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.EATING_HABITS,
        event: `${prefix}_${QUESTIONS_IDS.EATING_HABITS}`,
        type: QuestionTypes.Radio,
        title: i18n.t("eating_habits_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "emotional",
                text: i18n.t("emotional_eater"),
                description: i18n.t("emotional_eater_description"),
            },
            {
                id: "bored",
                text: i18n.t("bored_eater"),
                description: i18n.t("bored_eater_description"),
            },
            {
                id: "unconscious",
                text: i18n.t("unconscious_eater"),
                description: i18n.t("unconscious_eater_description"),
            },
            {
                id: "habitual",
                text: i18n.t("habitual_eater"),
                description: i18n.t("habitual_eater_description"),
            },
            {
                id: "energy",
                text: i18n.t("energy_eater"),
                description: i18n.t("energy_eater_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.COPE_STRESS,
        event: `${prefix}_${QUESTIONS_IDS.COPE_STRESS}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("cope_stress_prompt"),
        subtitle: i18n.t("cope_stress_motivation_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "exercising",
                text: i18n.t("exercising"),
            },
            {
                id: "being_creative",
                text: i18n.t("being_creative"),
            },
            {
                id: "bad_habits",
                text: i18n.t("bad_habits"),
            },
            {
                id: "eating",
                text: i18n.t("eating"),
            },
            {
                id: "outdoors",
                text: i18n.t("outdoors"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.SLEEP_AMOUNT,
        event: `${prefix}_${QUESTIONS_IDS.SLEEP_AMOUNT}`,
        type: QuestionTypes.Radio,
        title: i18n.t("sleep_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: SleepAmountAnswers.LessThan5,
                text: i18n.t("fewer_than_5_hours"),
            },
            {
                id: SleepAmountAnswers.From5To6,
                text: i18n.t("5_to_6_hours"),
            },
            {
                id: SleepAmountAnswers.From7To8,
                text: i18n.t("7_to_8_hours"),
            },
            {
                id: SleepAmountAnswers.Over8,
                text: i18n.t("over_8_hours"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DAILY_WALK,
        event: `${prefix}_${QUESTIONS_IDS.DAILY_WALK}`,
        type: QuestionTypes.Radio,
        title: i18n.t("daily_walk_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "few",
                text: i18n.t("less_than_1_hour"),
            },
            {
                id: "average",
                text: i18n.t("1_to_2_hours"),
            },
            {
                id: "lot",
                text: i18n.t("more_than_2_hours"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.WATER_CONSUMPTION,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.WATER_CONSUMPTION}`,
        title: i18n.t("water_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "no_water",
                text: i18n.t("only_tea_coffee"),
            },
            {
                id: "low",
                text: i18n.t("fewer_than_2_glasses"),
            },
            {
                id: "average",
                text: i18n.t("2_to_6_glasses"),
            },
            {
                id: "above_average",
                text: i18n.t("7_to_10_glasses"),
            },
            {
                id: "lot",
                text: i18n.t("more_than_10_glasses"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.DIET_TYPE,
        type: QuestionTypes.Radio,
        event: `${prefix}_dietary_preferences`,
        title: i18n.t("dietary_preferences_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "no_diet",
                text: i18n.t("none"),
            },
            {
                id: "diabetes",
                text: i18n.t("diabetes"),
            },
            {
                id: "vegan",
                text: i18n.t("vegetarian_vegan"),
            },
            {
                id: "gluten_free",
                text: i18n.t("gluten_free"),
            },
            {
                id: "lactose_free",
                text: i18n.t("lactose_free"),
            },
            {
                id: "nut_allergy",
                text: i18n.t("nut_allergy"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PLAN_FAILED,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.PLAN_FAILED}`,
        title: (
            <Trans
                i18nKey="fitness_plan_failure_prompt"
                values={{
                    goal: goalTextByIdChartImage[
                        answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
                    ],
                }}
            >
                <span className="text-zing-warm-neon-green-500" />
            </Trans>
        ),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "yes",
                text: i18n.t("yes"),
            },
            {
                id: "no",
                text: i18n.t("no"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.PROMO_HELP,
        progressBarBreakPoint: true,
        event: `${prefix}_${QUESTIONS_IDS.PROMO_HELP}`,
        type: QuestionTypes.PromoHelp,
    },
    {
        id: QUESTIONS_IDS.TRAINING_TRIGGER,
        event: `${prefix}_${QUESTIONS_IDS.TRAINING_TRIGGER}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("training_trigger_prompt"),
        subtitle: i18n.t("training_trigger_motivation_prompt"),
        groupTitle: questionGroups.Motivation,
        answers: [
            {
                id: "job_requirements",
                text: i18n.t("job_requirements"),
                description: i18n.t("job_requirements_description"),
            },
            {
                id: "observing_inactivity",
                text: i18n.t("observing_inactivity"),
                description: i18n.t("observing_inactivity_description"),
            },
            {
                id: "visual_indicators",
                text: i18n.t("visual_indicators"),
                description: i18n.t("visual_indicators_description"),
            },
            {
                id: "parental_stamina",
                text: i18n.t("parental_stamina"),
                description: i18n.t("parental_stamina_description"),
            },
            {
                id: "clothing_fit",
                text: i18n.t("clothing_fit"),
                description: i18n.t("clothing_fit_description"),
            },
            {
                id: "health_improvement",
                text: i18n.t("health_improvement"),
                description: i18n.t("health_improvement_description"),
            },
            {
                id: "aging_concerns",
                text: i18n.t("aging_concerns"),
                description: i18n.t("aging_concerns_description"),
            },
            {
                id: "current_fitness_levels",
                text: i18n.t("current_fitness_levels"),
                description: i18n.t("current_fitness_levels_description"),
            },
            {
                id: "sexual_performance",
                text: i18n.t("sexual_performance"),
                description: i18n.t("sexual_performance_description"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.OBSTACLES,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.OBSTACLES}`,
        title: (
            <Trans
                i18nKey="obstacle_prompt"
                values={{
                    goal: goalTextById[answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals],
                }}
            >
                <span className="text-zing-warm-neon-green-500" />
            </Trans>
        ),
        subtitle: i18n.t("obstacle_description"),
        groupTitle: questionGroups.Motivation,
        answers: [
            {
                id: "consistencyLack",
                text: i18n.t("lack_of_consistency"),
            },
            {
                id: "timeConstraints",
                text: i18n.t("time_constraints"),
            },
            {
                id: "lackOfMotivation",
                text: i18n.t("lack_of_motivation"),
            },
            {
                id: "fatigue",
                text: i18n.t("fatigue"),
            },
            {
                id: "lackOfVisibleProgress",
                text: i18n.t("lack_of_visible_progress"),
            },
            {
                id: "plateausInProgress",
                text: i18n.t("plateaus"),
            },
            {
                id: "lackOfSupport",
                text: i18n.t("lack_of_support"),
            },
            {
                id: "injuriesOrPain",
                text: i18n.t("injuries_or_pain"),
            },
        ],
    },
    {
        id: QUESTIONS_IDS.CALISTHENICS_OBSTACLE_FEEDBACK,
        type: QuestionTypes.CalisthenicsFeedbackLoops,
        groupTitle: questionGroups.Motivation,
        event: `${prefix}_${QUESTIONS_IDS.CALISTHENICS_OBSTACLE_FEEDBACK}`,
        img: kneeCalisthenics,
        ...getCalisthenicsTextByObstacle[
            answers?.[QUESTIONS_IDS.OBSTACLES] as keyof typeof getCalisthenicsTextByObstacle
        ],
    },
    ...((answers?.[QUESTIONS_IDS.OBSTACLES] as string[])?.includes("consistencyLack")
        ? [
              {
                  id: QUESTIONS_IDS.PREVENT_EXERCISE_CONSISTENTLY,
                  event: `${prefix}_${QUESTIONS_IDS.PREVENT_EXERCISE_CONSISTENTLY}`,
                  type: QuestionTypes.Checkbox,
                  title: i18n.t("prevent_exercise_consistently_prompt"),
                  subtitle: i18n.t("prevent_exercise_consistently_motivation_prompt"),
                  groupTitle: questionGroups.Motivation,
                  answers: [
                      {
                          id: "work_schedule",
                          text: i18n.t("work_schedule"),
                          description: i18n.t("work_schedule_description"),
                      },
                      {
                          id: "procrastination",
                          text: i18n.t("procrastination"),
                          description: i18n.t("procrastination_description"),
                      },
                      {
                          id: "unexpected_events",
                          text: i18n.t("unexpected_events"),
                          description: i18n.t("unexpected_events_description"),
                      },
                      {
                          id: "lack_of_accountability",
                          text: i18n.t("lack_of_accountability"),
                          description: i18n.t("lack_of_accountability_description"),
                      },
                  ],
              },
          ]
        : []),
    {
        id: QUESTIONS_IDS.EVENT_MOTIVATION,
        event: `${prefix}_${QUESTIONS_IDS.EVENT_MOTIVATION}`,
        type: QuestionTypes.Radio,
        title: (
            <Trans
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                i18nKey="important_event_prompt"
                values={{
                    goal: calisthenicsEventTextByGoal[
                        answers?.[QUESTIONS_IDS.PRIMARY_GOAL] as FitnessGoals
                    ],
                }}
                context={Genders.Male}
                defaultValue={i18n.t("important_event_prompt")}
            >
                <span className="text-zing-warm-neon-green-500" />
            </Trans>
        ),
        subtitle: i18n.t("important_event_motivation"),
        groupTitle: questionGroups.Motivation,
        answers: [
            {
                id: EventAnswers.Vacation,
                text: EventAnswersLabels.vacation,
            },
            {
                id: EventAnswers.Wedding,
                text: EventAnswersLabels.wedding,
            },
            {
                id: EventAnswers.Birthday,
                text: EventAnswersLabels.birthday,
            },
            {
                id: EventAnswers.SchoolReunion,
                text: EventAnswersLabels.school_reunion,
            },
            {
                id: EventAnswers.FamilyGathering,
                text: EventAnswersLabels.family_gathering,
            },
            {
                id: EventAnswers.None,
                text: EventAnswersLabels.none,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.EVENT_DATE,
        event: `${prefix}_${QUESTIONS_IDS.EVENT_DATE}`,
        type: QuestionTypes.Date,
        value: getDateOneMonthFromNow(),
        title:
            answers?.[QUESTIONS_IDS.EVENT_MOTIVATION] === EventAnswers.FamilyGathering
                ? i18n.t("event_timing_prompt")
                : answers?.[QUESTIONS_IDS.EVENT_MOTIVATION] === EventAnswers.Birthday
                  ? i18n.t("party_timing_prompt")
                  : i18n.t("conditional_event_timing_prompt", {
                        event: EventAnswersLabels[
                            answers?.[QUESTIONS_IDS.EVENT_MOTIVATION] as EventAnswers
                        ]?.toLowerCase(),
                    }),
        minDate: new Date().toISOString().split("T")[0],
        groupTitle: questionGroups.Motivation,
    },
    {
        id: QUESTIONS_IDS.ABILITY_TO_STAY_MOTIVATED,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.ABILITY_TO_STAY_MOTIVATED}`,
        title: i18n.t("ability_to_stay_motivated_prompt"),
        groupTitle: questionGroups.Lifestyle,
        answers: [
            {
                id: "steadfast",
                text: i18n.t("steadfast"),
                description: i18n.t("steadfast_description"),
                svg: steadfastSVG,
            },
            {
                id: "resilient",
                text: i18n.t("resilient"),
                description: i18n.t("resilient_description"),
                svg: resilientSVG,
            },
            {
                id: "challenging",
                text: i18n.t("challenging"),
                description: i18n.t("challenging_description"),
                svg: challengingSVG,
            },
            {
                id: "fickle",
                text: i18n.t("fickle"),
                description: i18n.t("fickle_description"),
                svg: fickleSVG,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.MOTIVATE_MOST,
        event: `${prefix}_${QUESTIONS_IDS.MOTIVATE_MOST}`,
        type: QuestionTypes.Checkbox,
        title: i18n.t("motivate_most_prompt"),
        subtitle: i18n.t("motivate_most_motivation_prompt"),
        groupTitle: questionGroups.Motivation,
        answers: [
            {
                id: "progress_tracking",
                text: i18n.t("progress_tracking"),
                description: i18n.t("progress_tracking_description"),
                svg: progressSVG,
            },
            {
                id: "feeling_better",
                text: i18n.t("feeling_better"),
                description: i18n.t("feeling_better_description"),
                svg: steadfastSVG,
            },
            {
                id: "achievements",
                text: i18n.t("achievements"),
                description: i18n.t("achievements_description"),
                svg: achievementsSVG,
            },
            {
                id: "discipline",
                text: i18n.t("discipline"),
                description: i18n.t("discipline_description"),
                svg: disciplineSVG,
            },
            {
                id: "music",
                text: i18n.t("music"),
                description: i18n.t("music_description"),
                svg: musicSVG,
            },
            {
                id: "challenges",
                text: i18n.t("challenges"),
                description: i18n.t("challenges_description"),
                svg: challengesSVG,
            },
        ],
    },
    {
        id: QUESTIONS_IDS.STATEMENT_REQUIRE_GUIDANCE,
        type: QuestionTypes.Rate,
        event: `${prefix}_${QUESTIONS_IDS.STATEMENT_REQUIRE_GUIDANCE}`,
        title: i18n.t("rate_prompt"),
        groupTitle: questionGroups.Motivation,
        subtitle: i18n.t("statement_require_guidance"),
        maxRate: 5,
    },
    {
        id: QUESTIONS_IDS.STATEMENT_SUPPORT_BOOSTS_MOTIVATION,
        type: QuestionTypes.Rate,
        event: `${prefix}_${QUESTIONS_IDS.STATEMENT_SUPPORT_BOOSTS_MOTIVATION}`,
        title: i18n.t("rate_prompt"),
        groupTitle: questionGroups.Motivation,
        subtitle: i18n.t("statement_support_boosts_motivation"),
        maxRate: 5,
    },
    {
        id: QUESTIONS_IDS.ACHIEVE_FITNESS_GOAL,
        type: QuestionTypes.Radio,
        event: `${prefix}_${QUESTIONS_IDS.ACHIEVE_FITNESS_GOAL}`,
        title: i18n.t("achieve_fitness_goal_prompt"),
        groupTitle: questionGroups.Motivation,
        answers: [
            {
                id: "highly_confident",
                text: i18n.t("highly_confident"),
                description: i18n.t("highly_confident_description"),
            },
            {
                id: "reasonably_confident",
                text: i18n.t("reasonably_confident"),
                description: i18n.t("reasonably_confident_description"),
            },
            {
                id: "optimistic",
                text: i18n.t("optimistic"),
                description: i18n.t("optimistic_description"),
            },
            {
                id: "not_confident",
                text: i18n.t("not_confident"),
                description: i18n.t("not_confident_description"),
            },
        ],
    },
];
